import React, { useState } from 'react';
import { Slider, Tooltip, useMediaQuery } from '@mui/material';
import { IoInformationCircleOutline, IoKeyOutline, IoStatsChartOutline } from 'react-icons/io5';
import { IMPACT_LEVELS, ENGAGEMENT_LEVELS } from '../../constants/impactLevels';
import { ImpactMetric, ImpactHistoryEntry } from '../../types/careerCard';
import { formatDate } from '../../utils/dateUtils';
import { useImpactMetricsKeyboard } from '../../hooks/useImpactMetricsKeyboard';
import MobileImpactControls from './MobileImpactControls';
import { TrendVisualizationModal } from './TrendVisualizationModal';

interface ImpactMetricsSectionProps {
  careerImpact: ImpactMetric;
  engagementLevel: ImpactMetric;
  onMetricChange: (type: 'career_impact' | 'engagement_level', value: number) => void;
  isEditMode?: boolean;
  cardType: string;
  historyEntries?: ImpactHistoryEntry[];
}

interface MobileImpactControlsProps {
  type: 'career_impact' | 'engagement_level';
  value: ImpactMetric;
  levels: typeof IMPACT_LEVELS | typeof ENGAGEMENT_LEVELS;
  onChange: (type: 'career_impact' | 'engagement_level', value: number) => void;
  colors: typeof CAREER_IMPACT_COLORS | typeof ENGAGEMENT_COLORS;
}

// Add color constants
const CAREER_IMPACT_COLORS = {
  1: '#FFE0B2',
  2: '#FFB74D',
  3: '#FFA726',
  4: '#FB8C00',
  5: '#EF6C00',
  primary: '#FFA726',
  hover: 'rgba(255, 167, 38, 0.16)',
  active: 'rgba(255, 167, 38, 0.16)'
} as const;

const ENGAGEMENT_COLORS = {
  1: '#B2DFDB',
  2: '#4DB6AC',
  3: '#26A69A',
  4: '#00897B',
  5: '#00695C',
  primary: '#26A69A',
  hover: 'rgba(38, 166, 154, 0.16)',
  active: 'rgba(38, 166, 154, 0.16)'
} as const;

const getTypeSpecificImpactLabel = (level: number, cardType: string): string => {
  const baseLabel = IMPACT_LEVELS[level as keyof typeof IMPACT_LEVELS]?.label || '';
  const typeSpecificLabels: Record<string, Record<number, string>> = {
    'ad-hoc-work': {
      1: 'Routine task with little growth potential',
      2: 'Small learning opportunity',
      3: 'Notable skill application',
      4: 'Major responsibility',
      5: 'Career-changing opportunity'
    },
    'career-goal': {
      1: 'Minor stepping stone',
      2: 'Useful but not critical goal',
      3: 'Important career milestone',
      4: 'Key career advancement',
      5: 'Transformative achievement'
    },
    'client': {
      1: 'Basic client interaction',
      2: 'Positive client engagement',
      3: 'Strong client relationship',
      4: 'Strategic client partnership',
      5: 'Game-changing relationship'
    },
    'colleague': {
      1: 'Casual professional contact',
      2: 'Helpful professional relationship',
      3: 'Valuable professional connection',
      4: 'Important mentor/connection',
      5: 'Career-defining connection'
    },
    'project': {
      1: 'Simple contribution',
      2: 'Valuable team contribution',
      3: 'Significant project role',
      4: 'Critical project leadership',
      5: 'Career-defining project'
    },
    'training': {
      1: 'Basic skill refresher',
      2: 'Useful skill enhancement',
      3: 'Valuable certification/skill',
      4: 'Advanced qualification',
      5: 'Industry-recognized expertise'
    }
  };

  const normalizedType = cardType.toLowerCase().replace(/\s+/g, '-');
  return typeSpecificLabels[normalizedType]?.[level] || baseLabel;
};

export const ImpactMetricsSection: React.FC<ImpactMetricsSectionProps> = ({
  careerImpact,
  engagementLevel,
  onMetricChange,
  isEditMode = false,
  cardType,
  historyEntries = []
}) => {
  const [isVisualizationOpen, setIsVisualizationOpen] = useState(false);

  // Ensure we have default values for new cards
  const safeCareerImpact = careerImpact || { value: 3, last_updated: new Date().toISOString() };
  const safeEngagementLevel = engagementLevel || { value: 3, last_updated: new Date().toISOString() };

  useImpactMetricsKeyboard({
    isEditMode,
    careerImpact: safeCareerImpact,
    engagementLevel: safeEngagementLevel,
    onMetricChange
  });

  const isMobile = useMediaQuery('(max-width: 640px)');

  // Get color values based on the metric type
  const getColorValues = (type: 'career_impact' | 'engagement_level') => {
    switch (type) {
      case 'career_impact':
        return CAREER_IMPACT_COLORS;
      case 'engagement_level':
        return ENGAGEMENT_COLORS;
      default:
        return CAREER_IMPACT_COLORS; // fallback
    }
  };

  const renderMetric = (
    type: 'career_impact' | 'engagement_level',
    value: ImpactMetric,
    levels: typeof IMPACT_LEVELS | typeof ENGAGEMENT_LEVELS
  ) => {
    const currentValue = value.value;
    const lastUpdated = value.last_updated;
    const label = type === 'career_impact' ? 'Career Impact' : 'Engagement Level';
    const description = type === 'career_impact'
      ? getTypeSpecificImpactLabel(currentValue, cardType)
      : levels[currentValue as keyof typeof levels]?.description;

    const shortcutHint = type === 'career_impact'
      ? 'Alt + ↑/↓'
      : 'Alt + ←/→';

    const colorValues = getColorValues(type);

    return (
      <div className="mb-2">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{label}</span>
            {isEditMode ? (
              <>
                <Tooltip 
                  title={description} 
                  arrow 
                  placement="right"
                  enterDelay={200}
                  leaveDelay={0}
                  PopperProps={{
                    modifiers: [{
                      name: 'offset',
                      options: {
                        offset: [0, 8],
                      },
                    }],
                  }}
                >
                  <div className="cursor-help">
                    <IoInformationCircleOutline className="text-gray-400" />
                  </div>
                </Tooltip>
                {!isMobile && (
                  <Tooltip 
                    title={`Keyboard shortcut: ${shortcutHint}`} 
                    arrow 
                    placement="right"
                    enterDelay={200}
                    leaveDelay={0}
                    PopperProps={{
                      modifiers: [{
                        name: 'offset',
                        options: {
                          offset: [0, 8],
                        },
                      }],
                    }}
                  >
                    <div className="flex items-center gap-1 text-xs text-gray-500">
                      <IoKeyOutline className="text-gray-400" />
                      <span>{shortcutHint}</span>
                    </div>
                  </Tooltip>
                )}
              </>
            ) : (
              <span className="text-sm text-gray-600 dark:text-gray-400">
                - {description}
              </span>
            )}
          </div>
          {lastUpdated && (
            <span className="text-xs text-gray-500">
              {formatDate(new Date(lastUpdated))}
            </span>
          )}
        </div>
        {isEditMode ? (
          isMobile ? (
            <div className="flex items-center gap-2">
              <div className="flex-1">
                <select
                  value={currentValue}
                  onChange={(e) => onMetricChange(type, parseInt(e.target.value))}
                  className="w-full px-3 py-2 border rounded bg-white border-gray-300 text-gray-900 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  {Object.entries(levels).map(([value, { label }]) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <Slider
              value={currentValue}
              min={1}
              max={5}
              step={1}
              marks
              onChange={(_, newValue) => onMetricChange(type, newValue as number)}
              valueLabelDisplay="auto"
              valueLabelFormat={(value) => levels[value as keyof typeof levels]?.label}
              className="mt-2"
              sx={{
                '& .MuiSlider-rail': {
                  opacity: 0.5,
                  backgroundColor: '#94a3b8',
                },
                '& .MuiSlider-track': {
                  border: 'none',
                  backgroundColor: colorValues.primary,
                },
                '& .MuiSlider-thumb': {
                  backgroundColor: colorValues.primary,
                  '&:hover, &.Mui-focusVisible': {
                    boxShadow: `0 0 0 8px ${colorValues.hover}`,
                  },
                  '&.Mui-active': {
                    boxShadow: `0 0 0 14px ${colorValues.active}`,
                  },
                },
                '& .MuiSlider-mark': {
                  backgroundColor: '#94a3b8',
                  height: 8,
                  width: 2,
                  marginTop: -3,
                  '&.MuiSlider-markActive': {
                    opacity: 1,
                    backgroundColor: colorValues.primary,
                  },
                },
                '& .MuiSlider-markLabel': {
                  fontSize: '0.75rem',
                  marginTop: 8,
                },
                height: 4,
              }}
            />
          )
        ) : (
          <div>
            <div className="flex items-center gap-2">
              <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                  className="h-full"
                  style={{ 
                    width: `${((currentValue - 1) / 4) * 100}%`,
                    backgroundColor: colorValues[currentValue as keyof typeof colorValues]
                  }}
                />
              </div>
              <span className="text-sm font-medium w-24 text-right">
                {levels[currentValue as keyof typeof levels]?.label}
              </span>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">Impact Metrics</h3>
        {!isEditMode && historyEntries.length > 0 && (
          <button
            onClick={() => setIsVisualizationOpen(true)}
            className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
          >
            <IoStatsChartOutline className="text-lg" />
            <span>View Trends</span>
          </button>
        )}
      </div>

      {renderMetric('career_impact', safeCareerImpact, IMPACT_LEVELS)}
      {renderMetric('engagement_level', safeEngagementLevel, ENGAGEMENT_LEVELS)}

      <TrendVisualizationModal
        isOpen={isVisualizationOpen}
        onClose={() => setIsVisualizationOpen(false)}
        historyEntries={historyEntries}
        title={`Impact Metrics History - ${cardType}`}
        onUpdateHistory={onMetricChange ? (entries) => {
          // Get the latest entry to update current metrics
          const latestEntry = entries[entries.length - 1];
          
          // First update the current metrics if this is the latest entry
          if (latestEntry) {
            onMetricChange('career_impact', latestEntry.career_impact, entries);
            onMetricChange('engagement_level', latestEntry.engagement_level, entries);
          }
        } : undefined}
      />
    </div>
  );
};


